import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderAPI, locationAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiPackage } from 'react-icons/fi';
import './OrderForm.css';

const PLATFORMS = [
  { id: 'swiggy', name: 'Swiggy', icon: '🍔', color: '#fc8019' },
  { id: 'zomato', name: 'Zomato', icon: '🍕', color: '#e23744' },
  { id: 'blinkit', name: 'Blinkit', icon: '⚡', color: '#f8cb46' },
  { id: 'bigbasket', name: 'BigBasket', icon: '🛒', color: '#84c225' },
  { id: 'amazon', name: 'Amazon', icon: '📦', color: '#ff9900' },
  { id: 'flipkart', name: 'Flipkart', icon: '🛍️', color: '#2874f0' },
  { id: 'other', name: 'Other', icon: '📱', color: '#666' },
];

const OrderForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    platform: '',
    platformOrderId: '',
    deliveryLocationId: '',
    expectedDelivery: '',
    notes: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getAll();
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handlePlatformSelect = (platformId) => {
    setFormData(prev => ({ ...prev, platform: platformId }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.platform) {
      toast.error('Please select a delivery platform');
      return;
    }

    if (!formData.platformOrderId) {
      toast.error('Please enter the order ID');
      return;
    }

    setLoading(true);
    try {
      const response = await orderAPI.create(formData);
      if (response.data.success) {
        toast.success('Order registered successfully!');
        navigate(`/orders/${response.data.order._id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to register order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="order-form-page">
      <div className="container">
        <button className="back-btn" onClick={() => navigate('/orders')}>
          <FiArrowLeft /> Back to Orders
        </button>

        <div className="page-header">
          <FiPackage className="page-icon" />
          <h1>Register New Order</h1>
          <p>Track your delivery from any platform</p>
        </div>

        <form onSubmit={handleSubmit} className="order-form card">
          <div className="form-section">
            <label className="section-label">Select Platform *</label>
            <div className="platform-grid">
              {PLATFORMS.map(platform => (
                <button
                  key={platform.id}
                  type="button"
                  className={`platform-option ${formData.platform === platform.id ? 'selected' : ''}`}
                  onClick={() => handlePlatformSelect(platform.id)}
                  style={formData.platform === platform.id ? {
                    borderColor: platform.color,
                    background: `${platform.color}15`
                  } : {}}
                >
                  <span className="platform-icon">{platform.icon}</span>
                  <span className="platform-name">{platform.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Order ID / Tracking Number *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., ORD123456789"
              value={formData.platformOrderId}
              onChange={(e) => setFormData(prev => ({ ...prev, platformOrderId: e.target.value }))}
            />
            <small className="form-hint">Enter the order ID from {formData.platform || 'the delivery app'}</small>
          </div>

          <div className="form-group">
            <label className="form-label">Delivery Location</label>
            <select
              className="form-input"
              value={formData.deliveryLocationId}
              onChange={(e) => setFormData(prev => ({ ...prev, deliveryLocationId: e.target.value }))}
            >
              <option value="">Select a saved location</option>
              {locations.map(loc => (
                <option key={loc._id} value={loc._id}>
                  {loc.label} - {typeof loc.address === 'string' ? loc.address.substring(0, 40) : (loc.address?.formatted || loc.address?.street || 'No address')}...
                </option>
              ))}
            </select>
            {locations.length === 0 && (
              <small className="form-hint">No saved locations. <a href="/locations/new">Add one</a></small>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Expected Delivery Time</label>
            <input
              type="datetime-local"
              className="form-input"
              value={formData.expectedDelivery}
              onChange={(e) => setFormData(prev => ({ ...prev, expectedDelivery: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notes (Optional)</label>
            <textarea
              className="form-input"
              placeholder="Any special instructions or notes about this order"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Registering...' : 'Register Order'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default OrderForm;
